import $ from 'jquery'
import { DragBox, Select as OLSelect } from 'ol/interaction'

import { Style, Stroke } from 'ol/style'
import * as olHas from 'ol/has'
import * as olConditions from 'ol/events/condition'

//Documentation
import { Pixel } from 'ol/pixel'
import { Extent } from 'ol/extent'
import Collection from 'ol/Collection'
import MapBrowserEvent from 'ol/MapBrowserEvent'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'

/**
 * @module
 * @api
 */

import Tool from '../Tool'

import { createDefaultEditingStyles } from '../ol'
import { Layer } from 'ol/layer'

export function getSelectStyleFunction() {
  var styles = createDefaultEditingStyles()
  styles['Polygon'] = styles['LineString']
  styles['MultiPolygon'] = styles['LineString']
  return function(feature, resolution) {
    const org = feature.layer_.getStyle()(feature, resolution)
    const sel = styles[feature.getGeometry().getType()]
    return org.concat(sel)
  }
}

/**
 * Select tool class
 *
 * @param {Object} config Olga configuration object
 */
export default class Select extends Tool {
  constructor(config) {
    super()

    $.extend(this, config)
    this.enabled = false
    this.enableToggle = true

    this.toggleButton = $('#selectToggle')

    this.select = new OLSelect({
      style: getSelectStyleFunction(),
      condition: olConditions.never
    })

    this.deselectButton = $('#selectClearAll')

    this.deselectButton.on('click', this.handleDeselectButtonClick.bind(this))

    this.selectedFeatures = this.select.getFeatures()

    // a DragBox interaction used to select features by drawing boxes
    this.dragBox = new DragBox({
      condition: olConditions.shiftKeyOnly,
      style: new Style({
        stroke: new Stroke({
          color: [0, 0, 255, 1]
        })
      })
    })

    this.map.on('click', this.handleSelectClick.bind(this))

    this.eventListeners = []
    this.stashedListeners = []

    this.dragBox.on('boxend', this.handleDragboxEnd.bind(this))

    this.select.getFeatures().on('remove', this.handleSelectChange.bind(this))

    this.select.getFeatures().on('add', this.handleSelectChange.bind(this))

    this.disableDeselect = false
  }

  /**
   * Activate select control
   * @memberof! module:api/Select#
   * @api
   */
  activate() {
    if (!this.activated) {
      $('.digitizing-select').removeClass('hide')
      this.map.addInteraction(this.select)
      this.map.addInteraction(this.dragBox)
      super.activate()
      this.activated = true
    }
  }

  /**
   * Deactivate select control
   * @memberof! module:api/Select#
   * @api
   */
  deactivate() {
    if (this.activated) {
      this.clear()
      this.map.removeInteraction(this.select)
      this.map.removeInteraction(this.dragBox)
      super.deactivate()
      this.activated = false
    }
  }
}
/**
 * Help function to get selectable vector layers from layer
 * @returns {Object<string,Layer>}
 */

Select.prototype.getSelectableConfigLayers = function() {
  return this.geometry.layer
    .getConfigLayers()
    .filter(
      l =>
        l.type === 'Vector' &&
        l.source &&
        l.source.type === 'Vector' &&
        l.metadata &&
        l.metadata.select
    )
    .reduce((acc, curr) => {
      return Object.assign(acc, { [curr.name]: curr.mapLayer_ })
    }, {})
}

/**
 * Enable user selects
 * @memberof! module:api/Select#
 * @api
 */
Select.prototype.enable = function() {
  this.enabled = true
  if (!this.activated) {
    this.toggleButton = $('#selectToggle')
    this.toggleButton.trigger('click')
  }
}

/**
 * Disable user selects
 * @memberof! module:api/Select#
 * @api
 */
Select.prototype.disable = function() {
  this.enabled = false
  if (this.activated) {
    this.toggleButton = $('#selectToggle')
    this.toggleButton.trigger('click')
  }
}

/**
 * Clear all selections
 * @memberof! module:api/Select#
 * @api
 */
Select.prototype.clear = function() {
  this.selectedFeatures.clear()
}

/**
 * Add selection change event listener
 * @param {function} callback with an CollectionEvent as first argument
 * @memberof! module:api/Select#
 * @api
 */
Select.prototype.onChange = function(callback) {
  this.eventListeners.push(callback)
}

/**
 * Select geometries by property
 * @param {string|string[]} property Property name or an array of properties
 * @param {*} value Value or an array of values
 * @memberof! module:api/Select#
 * @api
 */
Select.prototype.selectByProperty = function(property, value) {
  var selectableConfigLayers = this.getSelectableConfigLayers()
  var apiLayers = Object.assign(
    {},
    this.geometry.getLayers(),
    selectableConfigLayers
  )
  for (var layerKey in apiLayers) {
    var features = this.geometry.getFeatureByProperty(
      apiLayers[layerKey],
      property,
      value
    )

    for (var featureKey in features) {
      this.addFeatureToSelection(features[featureKey], true)
    }
  }
}

/**
 * Deselect geometries by property
 * @param {string|string[]} property Property name or an array of properties
 * @param {*} value Value or an array of values
 * @memberof! module:api/Select#
 * @api
 */
Select.prototype.deselectByProperty = function(property, value) {
  var selectableConfigLayers = this.getSelectableConfigLayers()
  var apiLayers = Object.assign(
    {},
    this.geometry.getLayers(),
    selectableConfigLayers
  )

  for (var layerKey in apiLayers) {
    var features = this.geometry.getFeatureByProperty(
      apiLayers[layerKey],
      property,
      value
    )

    for (var featureKey in features) {
      var feature = features[featureKey]
      this.removeFeatureFromSelection(feature)
    }
  }
}

/**
 * Gets all selected features
 * @return {Collection} Collection
 * @memberof! module:api/Select#
 * @api
 */
Select.prototype.getSelectedFeatures = function() {
  return this.selectedFeatures
}

/**
 * Handle select click
 * @param {MapBrowserEvent} e
 */
Select.prototype.handleSelectClick = function(e) {
  if (!this.activated) {
    return
  }
  e.preventDefault()
  e.stopPropagation()
  var selectableConfigLayers = this.getSelectableConfigLayers()
  var extent = this.createExtentFromPixel(e.pixel)

  var apiLayers = Object.assign(
    {},
    this.geometry.getLayers(),
    selectableConfigLayers
  )

  for (var key in apiLayers) {
    var vectorSource = apiLayers[key].getSource()
    this.parseVectorSource(extent, vectorSource)
  }
}

/**
 * Parse vector source
 * @param {Extent} extent
 * @param {VectorSource} vectorSource
 */
Select.prototype.parseVectorSource = function(extent, vectorSource) {
  var selectedFeatures = []
  var unselectedFeatures = []

  vectorSource.forEachFeatureIntersectingExtent(
    extent,
    function(f) {
      if (this.featureIsSelected(f)) {
        selectedFeatures.push(f)
      } else {
        unselectedFeatures.push(f)
      }
    }.bind(this)
  )

  if (selectedFeatures.length > 0) {
    // Deselect all if one feature is selected
    selectedFeatures.forEach(
      function(f) {
        this.removeFeatureFromSelection(f)
      }.bind(this)
    )
  } else {
    // Select all if no feature is selected

    unselectedFeatures.forEach(
      function(f) {
        this.addFeatureToSelection(f)
      }.bind(this)
    )
  }
}

/**
 * Get extent from pixel
 * @param {Pixel} pixel
 * @return {Extent} extent
 */
Select.prototype.createExtentFromPixel = function(pixel) {
  var extent
  var devicePixelRatio = olHas.DEVICE_PIXEL_RATIO
  var bufferPixels = 5
  var buffer = bufferPixels * devicePixelRatio
  var pixelX = pixel[0]
  var pixelY = pixel[1]

  var pixelBottomLeft = [pixelX - buffer, pixelY + buffer]
  var pixelTopRight = [pixelX + buffer, pixelY - buffer]

  var coordinateBottomLeft = this.map.getCoordinateFromPixel(pixelBottomLeft)
  var coordinateTopRight = this.map.getCoordinateFromPixel(pixelTopRight)

  extent = [
    coordinateBottomLeft[0],
    coordinateBottomLeft[1],
    coordinateTopRight[0],
    coordinateTopRight[1]
  ]

  return extent
}

/**
 * Handle select change
 * @param {MapBrowserEvent} e
 */
Select.prototype.handleSelectChange = function(e) {
  // Call change event flisteners added via API
  this.eventListeners.forEach(function(listener) {
    listener(e)
  })

  var selectedFeatures = this.getSelectedFeatures()
  var numberOfSelectedFeatures = selectedFeatures.get('length')

  if (numberOfSelectedFeatures > 0) {
    this.deselectButton.removeClass('hide')
    this.deselectButton.find('.badge').text(numberOfSelectedFeatures)
  } else {
    this.deselectButton.addClass('hide')
  }
}

/**
 * Add all features in selected extent on dragbox end
 */
Select.prototype.handleDragboxEnd = function() {
  var extent = this.dragBox.getGeometry().getExtent()
  var selectableConfigLayers = this.getSelectableConfigLayers()
  var apiLayers = Object.assign(
    {},
    this.geometry.getLayers(),
    selectableConfigLayers
  )
  for (var key in apiLayers) {
    var vectorSource = apiLayers[key].getSource()

    vectorSource.forEachFeatureIntersectingExtent(
      extent,
      function(feature) {
        this.addFeatureToSelection(feature)
      }.bind(this)
    )
  }
}

/**
 * Handle deselect button click
 */
Select.prototype.handleDeselectButtonClick = function() {
  this.clear()
}

/**
 * Check if feature is selected
 * @param {Feature} feature
 * @return {bool} featureAlreadyExists
 */
Select.prototype.featureIsSelected = function(feature) {
  var featureIsSelected = false

  var selectedFeatures = this.selectedFeatures.getArray()
  if (selectedFeatures.indexOf(feature) > -1) {
    featureIsSelected = true
  }

  return featureIsSelected
}

/**
 * Add feature to selection
 * @param {Feature} feature
 */
Select.prototype.addFeatureToSelection = function(feature) {
  if (!this.featureIsSelected(feature)) {
    this.selectedFeatures.push(feature)
  }
}

/**
 * Add feature to selection
 * @param {Feature} feature
 */
Select.prototype.removeFeatureFromSelection = function(feature) {
  this.selectedFeatures.remove(feature)
}

Select.prototype.stashListeners = function() {
  this.stashedListeners = this.eventListeners
  this.eventListeners = []
}

Select.prototype.popListeners = function() {
  this.eventListeners = this.stashedListeners
  this.stashedListeners = []
}
